import React, { useEffect, useState } from "react";
import PageHeader from "../components/PageHeader";
import MediaPickerModal from "../components/MediaPickerModal";
import { AccessLevels, createRole } from "../action/role";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import validator from "validator";

const CreateNewRole = () => {
  const navigate = useNavigate();
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [banner, setBanner] = useState("");
  const [accessLevels, setAccessLevels] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [roleName, setRoleName] = useState("");
  const [desc, setDesc] = useState("");
  const [errors, setErrors] = useState({});
  const fetchRoles = async () => {
    try {
      const res = await AccessLevels();
      if (res?.status) {
        const items = res.data.items;
        setAccessLevels(items);

        // initialize permissions
        const initialState = {};
        items.forEach((item, index) => {
          initialState[item._id] = {
            is_active: index === 0, // enable first access level
            create: false,
            edit: false,
            view: true, // view always checked
            all: false,
          };
        });
        setPermissions(initialState);
      }
    } catch (error) {
      console.log("Error");
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const handleMainToggle = (id) => {
    setPermissions((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        is_active: !prev[id].is_active,
      },
    }));
  };

  const handleCheckbox = (id, type) => {
    setPermissions((prev) => {
      const current = prev[id];

      if (type === "all") {
        const newValue = !current.all;
        return {
          ...prev,
          [id]: {
            ...current,
            all: newValue,
            create: newValue,
            edit: newValue,
            view: true,
          },
        };
      }

      const updated = {
        ...current,
        [type]: !current[type],
      };

      // auto check ALL if C + E + V true
      if (updated.create && updated.edit && updated.view) {
        updated.all = true;
      } else {
        updated.all = false;
      }

      return {
        ...prev,
        [id]: updated,
      };
    });
  };

  const buildOperationString = (perm) => {
    if (!perm.is_active) return "R";

    if (perm.all) return "CRUD";

    let op = "";
    if (perm.create) op += "C";
    if (perm.view) op += "R";
    if (perm.edit) op += "U";

    return op || "R";
  };

  const validateForm = () => {
    let newErrors = {};

    if (!validator.trim(roleName || "")) {
      newErrors.roleName = "Role name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    try {
      if (!validateForm()) return;
      const user_access_levels = accessLevels.map((item) => ({
        access_level_id: item._id,
        operation: buildOperationString(permissions[item._id]),
        is_active: permissions[item._id]?.is_active || false,
      }));

      const payload = {
        name: roleName,
        description: desc,
        user_access_levels,
      };

      const res = await createRole(payload);
      if (res.status) {
        toast.success(res.message);
        navigate("/role");
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      toast.error("Falied to add role");
    }
  };

  return (
    <>
      <PageHeader pageTitle="Create New Role" is_deploy_enable={false}>
        <button className="btn-white me-3">Discard</button>
        <button className="btn-black" onClick={handleSubmit}>
          Create Role
        </button>
      </PageHeader>

      <div className="page-container">
        <div className="row">
          <div className="col-lg-6">
            <div className="theme-card p-4 mb-4">
              <div className="card-title pb-3">Role Information</div>

              <div className="frm-group">
                <label className="frm-label">Role Name</label>
                <input
                  type="text"
                  className="frm-input"
                  placeholder="Enter role name"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  required
                />
                {errors.roleName && (
                  <div className="text-danger mt-1">{errors.roleName}</div>
                )}
              </div>

              <div className="frm-group">
                <label className="frm-label">Description</label>
                <textarea
                  rows={3}
                  className="frm-input"
                  placeholder="Enter description"
                  value={desc}
                  onChange={(e) => setDesc(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="theme-card p-4 mb-4">
              <div className="card-title pb-3">Access Levels</div>

              {accessLevels.map((l, i) => {
                const perm = permissions[l._id] || {};

                return (
                  <div
                    key={l._id}
                    className="d-flex justify-content-between py-3 border-bottom permission_card"
                  >
                    <div className="permission_box">
                      <p className="permission_ttl">{l.menu}</p>
                      <p className="permission_sub_ttl">
                        {l.desc ||
                          "Create, edit, assign and track project-level information."}
                      </p>

                      {perm.is_active && (
                        <div className="check_boxes_container">
                          <div className="common_check">
                            <label>
                              All
                              <input
                                type="checkbox"
                                checked={perm.all}
                                onChange={() => handleCheckbox(l._id, "all")}
                              />
                            </label>
                          </div>

                          <div className="common_check">
                            <label>
                              Create
                              <input
                                type="checkbox"
                                checked={perm.create}
                                onChange={() => handleCheckbox(l._id, "create")}
                              />
                            </label>
                          </div>

                          <div className="common_check">
                            <label>
                              Edit
                              <input
                                type="checkbox"
                                checked={perm.edit}
                                onChange={() => handleCheckbox(l._id, "edit")}
                              />
                            </label>
                          </div>

                          <div className="common_check">
                            <label>
                              View
                              <input type="checkbox" checked={true} disabled />
                            </label>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex-shrink-0 pt-1">
                      <label className="custom-switch">
                        <input
                          type="checkbox"
                          checked={perm.is_active}
                          onChange={() => handleMainToggle(l._id)}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <MediaPickerModal
        show={showMediaPicker}
        onClose={() => setShowMediaPicker(false)}
        onSelect={(mediaUrl) => {
          setBanner(mediaUrl);
          setShowMediaPicker(false);
        }}
      />
    </>
  );
};

export default CreateNewRole;
